/**
 * ProgressReporter - Aggregates activity progress for dashboards.
 *
 * Provides computed views of progress data for the ActivityHub,
 * parent/teacher dashboards, and analytics.
 *
 * @see GitHub Issue #852
 */

import ActivityStorageService from './ActivityStorageService';

export default class ProgressReporter {
    /**
     * Get summary stats for the ActivityHub display.
     * @param {object} curriculum - Loaded curriculum manifest
     * @returns {Promise<object>}
     */
    static async getHubSummary (curriculum) {
        const progress = await ActivityStorageService.getProgress();
        const badges = await ActivityStorageService.getEarnedBadges();
        const totalStars = Object.values(progress).reduce((s, p) => s + (p.stars || 0), 0);

        const courses = [];
        for (const course of (curriculum.courses || [])) {
            const activityIds = ProgressReporter._extractActivityIds(course);
            const completed = activityIds.filter(id => progress[id] && progress[id].status === 'completed');
            courses.push({
                courseId: course.courseId,
                title: course.title,
                totalActivities: activityIds.length,
                completedActivities: completed.length,
                progressPercent: activityIds.length > 0 ? Math.round(completed.length / activityIds.length * 100) : 0
            });
        }

        return {
            totalStars,
            badgeCount: badges.length,
            courses,
            nextActivity: await ProgressReporter.getNextIncomplete(curriculum)
        };
    }

    /**
     * Get the next incomplete activity (for "Continue" button).
     * @param {object} curriculum
     * @returns {Promise<object|null>} {activityId, path, title} or null
     */
    static async getNextIncomplete (curriculum) {
        const progress = await ActivityStorageService.getProgress();

        for (const course of (curriculum.courses || [])) {
            for (const unit of (course.units || [])) {
                for (const lesson of (unit.lessons || [])) {
                    if (ProgressReporter._isLessonLocked(lesson, progress)) {
                        continue;
                    }
                    const p = progress[lesson.activityId];
                    if (!p || p.status !== 'completed') {
                        return {
                            activityId: lesson.activityId,
                            path: `courses/${course.courseId}/units/${unit.unitId}/${lesson.file}`,
                            title: lesson.title || lesson.activityId,
                            courseId: course.courseId,
                            unitId: unit.unitId
                        };
                    }
                }
            }
        }

        return null; // All complete
    }

    /**
     * Get detailed progress for a single course.
     * @param {object} course - Course manifest data
     * @returns {Promise<object>}
     */
    static async getCourseDetail (course) {
        const progress = await ActivityStorageService.getProgress();

        const units = [];
        for (const unit of (course.units || [])) {
            const lessons = [];
            for (const lesson of (unit.lessons || [])) {
                const p = progress[lesson.activityId] || {};
                lessons.push({
                    activityId: lesson.activityId,
                    title: lesson.title || lesson.activityId,
                    file: lesson.file,
                    status: p.status || 'not_started',
                    stars: p.stars || 0,
                    attempts: p.attempts || 0,
                    locked: ProgressReporter._isLessonLocked(lesson, progress)
                });
            }

            const completed = lessons.filter(l => l.status === 'completed').length;
            units.push({
                unitId: unit.unitId,
                title: unit.title,
                lessons,
                completedCount: completed,
                totalCount: lessons.length,
                progressPercent: lessons.length > 0 ? Math.round(completed / lessons.length * 100) : 0
            });
        }

        return {courseId: course.courseId, title: course.title, units};
    }

    /**
     * Get CT concept mastery data (for parent/teacher dashboard).
     * @param {Array} activities - All activity definitions (with metadata.ctConcepts)
     * @returns {Promise<object>} Map of concept → {practiced, mastered}
     */
    static async getCTMastery (activities) {
        const progress = await ActivityStorageService.getProgress();
        const mastery = {};

        for (const activity of activities) {
            const concepts = (activity.metadata && activity.metadata.ctConcepts) || [];
            const p = progress[activity.activityId];

            for (const concept of concepts) {
                if (!mastery[concept]) {
                    mastery[concept] = {practiced: 0, mastered: 0, total: 0};
                }
                mastery[concept].total++;
                if (p && p.status === 'completed') {
                    mastery[concept].practiced++;
                    if (p.stars >= 2) {
                        mastery[concept].mastered++;
                    }
                }
            }
        }

        return mastery;
    }

    /**
     * Extract all activity IDs from a course manifest.
     * @private
     */
    static _extractActivityIds (course) {
        const ids = [];
        for (const unit of (course.units || [])) {
            for (const lesson of (unit.lessons || [])) {
                ids.push(lesson.activityId);
            }
        }
        return ids;
    }

    static _isLessonLocked (lesson, progress) {
        if (!lesson) {
            return false;
        }

        const flags = typeof window !== 'undefined' ? window.__FF : null;
        if (flags && flags.unlockAllActivities === true) {
            return false;
        }

        if (ProgressReporter._allowLocalTreasureMapUnlock(lesson)) {
            return false;
        }

        if (!lesson.unlockRequires) {
            return false;
        }

        const prerequisite = progress && progress[lesson.unlockRequires];
        return !(prerequisite && prerequisite.status === 'completed');
    }

    static _allowLocalTreasureMapUnlock (lesson) {
        if (!lesson || lesson.activityId !== 'treasure-map') {
            return false;
        }

        const flags = typeof window !== 'undefined' ? window.__FF : null;
        return ProgressReporter._isLocalRuntime() && !!(flags && flags.activity4TreasureMap === true);
    }

    static _isLocalRuntime () {
        const hostname = typeof window !== 'undefined' && window.location
            ? window.location.hostname || ''
            : '';
        return hostname === 'localhost' || hostname === '127.0.0.1';
    }
}
